import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

export default function AdminSignup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", setup_code: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form, { withCredentials: true });
      toast.success("Account बन गया — Owner Dashboard खोल रहे हैं");
      setTimeout(() => navigate("/admin"), 700);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-28" data-testid="admin-signup-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        onSubmit={submit}
        className="card-glow w-full max-w-md rounded-2xl p-8 sm:p-10"
        data-testid="admin-signup-form"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <UserPlus size={20} />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold uppercase text-white">Owner signup</h1>
        <p className="mt-2 text-sm text-neutral-400">नया owner account बनाएँ — dashboard में सारी bookings दिखेंगी।</p>

        <div className="mt-7 space-y-4">
          <div>
            <label htmlFor="su-name" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              नाम
            </label>
            <input id="su-name" data-testid="signup-name-input" type="text" className={inputCls} value={form.name} onChange={set("name")} placeholder="Ishu" minLength={2} required />
          </div>
          <div>
            <label htmlFor="su-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              Email
            </label>
            <input id="su-email" data-testid="signup-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="owner@ishumotors.com" required />
          </div>
          <div>
            <label htmlFor="su-pass" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              Password (min 8 characters)
            </label>
            <input id="su-pass" data-testid="signup-password-input" type="password" className={inputCls} value={form.password} onChange={set("password")} minLength={8} required />
          </div>
          <div>
            <label htmlFor="su-code" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
              Setup code
            </label>
            <input id="su-code" data-testid="signup-code-input" type="password" className={inputCls} value={form.setup_code} onChange={set("setup_code")} placeholder="Owner setup code" required />
            <p className="mt-2 flex items-start gap-2 text-[11px] leading-relaxed text-neutral-500">
              <Info size={12} className="mt-0.5 shrink-0 text-[#e6b800]" />
              Setup code सिर्फ़ owner के पास है — इसी से नए admin accounts सुरक्षित रहते हैं।
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="signup-submit-button"
            className="w-full rounded-full bg-[#e6b800] py-3.5 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:opacity-50"
          >
            {loading ? "Creating..." : "Create owner account"}
          </button>
        </div>

        <div className="mt-6 flex items-center justify-between text-xs">
          <Link to="/admin" data-testid="signup-login-link" className="text-neutral-400 transition-colors hover:text-[#e6b800]">
            ← Login पर वापस
          </Link>
          <Link to="/admin/forgot" data-testid="signup-forgot-link" className="text-neutral-400 transition-colors hover:text-[#e6b800]">
            Password भूल गए?
          </Link>
        </div>
      </motion.form>
    </div>
  );
}
